import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Tag, Save } from "lucide-react";
import { toast } from "sonner";
import MultiSelect from "@/components/MultiSelect";

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#0ea5e9", "#6366f1", "#a855f7", "#ec4899",
];

export default function QuizTypesTab() {
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [form, setForm] = useState({ name: "", color: "#0EA5E9", default_department_ids: [] });
  const [editing, setEditing] = useState(null); // id or null

  async function load() {
    const [t, d] = await Promise.all([
      api.get("/masters/quiz-types"),
      api.get("/masters/departments"),
    ]);
    setItems(t.data); setDepts(d.data);
  }
  useEffect(() => { load(); }, []);

  function reset() {
    setForm({ name: "", color: "#0EA5E9", default_department_ids: [] });
    setEditing(null);
  }

  async function save() {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await api.put(`/masters/quiz-types/${editing}`, form);
        toast.success("Updated");
      } else {
        await api.post("/masters/quiz-types", form);
        toast.success("Created");
      }
      reset(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  async function remove(id) {
    if (!confirm("Delete this quiz type?")) return;
    try {
      await api.delete(`/masters/quiz-types/${id}`);
      toast.success("Deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  function startEdit(t) {
    setEditing(t.id);
    setForm({
      name: t.name,
      color: t.color || "#0EA5E9",
      default_department_ids: t.default_department_ids || [],
    });
  }

  function toggleDept(id) {
    const cur = new Set(form.default_department_ids);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setForm({ ...form, default_department_ids: Array.from(cur) });
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-500">
        Group quizzes by <span className="font-bold text-slate-700">type</span> (Mandatory, Onboarding, Refresher, Compliance, etc.).
        Pre-select <span className="font-bold text-slate-700">default departments</span> for one-click bulk assignment from the Assignments page.
      </div>

      <Card className="border-slate-200 rounded-2xl" data-testid="quiz-type-form">
        <CardContent className="p-5 space-y-4">
          <div className="font-display font-black text-lg flex items-center gap-2">
            <Tag className="h-5 w-5 text-sky-500"/>{editing ? "Edit Quiz Type" : "New Quiz Type"}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name *</Label>
              <Input value={form.name} onChange={(e)=>setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Mandatory, Onboarding…" data-testid="qt-name"/>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Color</Label>
              <div className="flex flex-wrap gap-1.5">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" onClick={()=>setForm({ ...form, color: c })}
                    className={`h-7 w-7 rounded-full border-2 transition ${form.color === c ? "border-slate-900 scale-110" : "border-white shadow"}`}
                    style={{ background: c }} data-testid={`qt-color-${c.slice(1)}`}/>
                ))}
                <Input type="color" value={form.color} onChange={(e)=>setForm({ ...form, color: e.target.value })}
                  className="h-7 w-12 p-0.5 cursor-pointer"/>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Default Departments (for bulk-assign)</Label>
            <MultiSelect
              value={form.default_department_ids || []}
              onChange={(ids) => setForm({ ...form, default_department_ids: ids })}
              options={depts}
              placeholder={depts.length ? "Search & pick departments…" : "No departments yet."}
              testid="qt-dept"
              chipsLabel="department(s) selected"
            />
            <div className="text-[11px] text-slate-400">Tip: Pre-tagged departments appear pre-selected when you use &ldquo;Bulk Assign by Type&rdquo; on the Assignments page.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={save} className="bg-sky-500 hover:bg-sky-600 rounded-xl" data-testid="qt-save"><Save className="h-4 w-4 mr-1.5"/>{editing ? "Update" : "Create"}</Button>
            {editing && <Button variant="outline" onClick={reset} className="rounded-xl" data-testid="qt-cancel">Cancel</Button>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-5">
          <div className="font-display font-black text-lg mb-3">Quiz Types ({items.length})</div>
          <div className="space-y-2">
            {items.length === 0 && <div className="text-sm text-slate-400 italic">No quiz types yet — add your first one above.</div>}
            {items.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50" data-testid={`qt-row-${t.id}`}>
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: t.color }}/>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {(t.default_department_ids || []).length} default dept(s) · used by quizzes
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={()=>startEdit(t)} className="h-8 rounded-lg" data-testid={`qt-edit-${t.id}`}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={()=>remove(t.id)} className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`qt-del-${t.id}`}>
                  <Trash2 className="h-4 w-4"/>
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
